/**
 * Component registry for the layout rendering engine.
 *
 * Maps node kind + type identifiers to the React component that renders them.
 * The renderer looks up each LayoutNode in this registry to decide which
 * component to instantiate.
 */

import type { ComponentType } from "react";
import type { LayoutNodeRenderProps } from "./renderer-types";

export type RegistryEntry = {
  component: ComponentType<LayoutNodeRenderProps>;
};

const registry = new Map<string, RegistryEntry>();

/**
 * Registers a renderer for a given node key.
 *
 * @param key - A unique key, e.g. "primitive:text" or "component:layoutList".
 * @param entry - The registry entry containing the React component.
 */
export function registerRenderer(key: string, entry: RegistryEntry): void {
  registry.set(key, entry);
}

/**
 * Looks up the registered renderer for a node key.
 *
 * @param key - The key to look up.
 * @returns The registry entry, or undefined if not registered.
 */
export function getRenderer(key: string): RegistryEntry | undefined {
  return registry.get(key);
}

/**
 * Builds the registry key for a LayoutNode.
 *
 * @example
 * nodeKey({ kind: "primitive", primitive: "text" }) // "primitive:text"
 * nodeKey({ kind: "component", component: "layoutList" }) // "component:layoutList"
 */
export function nodeKey(node: {
  kind: string;
  primitive?: string;
  component?: string;
}): string {
  if (node.kind === "primitive") return `primitive:${node.primitive}`;
  if (node.kind === "component") return `component:${node.component}`;
  return `container`;
}
